import clsx from "classnames";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { StateOption } from "../../../constants/state";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { Icon } from "../../core/Icon/Icon";
import { Typography } from "../../core/Typography/Typography";
import StateDropDown from "../StateDropDown/StateDropDown";
import { useStyles } from "./BillingInfoPopup.style";

type BillingSelectProps = {
  id: string;
  label: string;
  placeholder: string;
  options: StateOption[];
  value: string;
  onChange: (value: string) => void;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  gamepadId?: string;
  disabled?: boolean;
};

export const BillingSelect: React.FC<BillingSelectProps> = ({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  isOpen,
  onOpenChange,
  gamepadId,
  disabled,
}) => {
  const classes = useStyles();
  const { setActiveElement } = useGamepadContext();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerRect, setTriggerRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const selectedLabel = options.find((option) => option.value === value)
    ?.label;

  useLayoutEffect(() => {
    if (!isOpen) return;
    const update = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setTriggerRect({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };
    update();
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !triggerRect) return;
    const idx = Math.max(
      0,
      options.findIndex((option) => option.value === value),
    );
    const option = document.querySelector<HTMLElement>(
      `[data-gamepad="state-${idx}"]`,
    );
    if (option) {
      option.setAttribute("data-gamepad-no-auto-scroll", "true");
      setActiveElement(option);
      option.removeAttribute("data-gamepad-no-auto-scroll");
      option.scrollIntoView?.({ block: "nearest", behavior: "instant" });
    }
  }, [isOpen, value, options, setActiveElement, triggerRect]);

  const toggle = () => {
    if (disabled) return;
    onOpenChange(!isOpen);
  };

  const select = (option: { value: string }) => {
    onChange(option.value);
    onOpenChange(false);
    if (triggerRef.current) setActiveElement(triggerRef.current);
  };

  return (
    <div className={classes.field}>
      <Typography
        variant={TypographyVariant.BodyB5Regular}
        color={TypographyColor.Silver}
        className={classes.label}
      >
        {label}
      </Typography>
      <div
        ref={triggerRef}
        className={clsx(classes.selectControl, {
          [classes.selectControlDisabled]: disabled,
        })}
        onClick={toggle}
        role="combobox"
        tabIndex={-1}
        aria-expanded={isOpen}
        aria-controls={isOpen ? id : undefined}
        aria-haspopup="listbox"
        aria-label={label}
        aria-disabled={disabled || undefined}
        data-gamepad={disabled ? undefined : gamepadId}
        data-selected-class={classes.selectControlActive}
      >
        <Typography
          variant={TypographyVariant.BodyB5Regular}
          color={
            selectedLabel ? TypographyColor.Default : TypographyColor.Gray
          }
        >
          {selectedLabel || placeholder}
        </Typography>
        <Icon
          name="icon-arrow-down"
          size={16}
          className={clsx(classes.selectIcon, {
            [classes.selectIconOpen]: isOpen,
          })}
        />
      </div>
      {isOpen && triggerRect && (
        <StateDropDown
          id={id}
          options={options}
          value={value}
          onSelectState={select}
          className={classes.dropdown}
          style={{
            top: triggerRect.top,
            left: triggerRect.left,
            width: triggerRect.width,
          }}
        />
      )}
    </div>
  );
};
